import { PersonAdd } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MessageIcon from "@mui/icons-material/Message";
import { Box, Button, Fab } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { INK, NEUTRAL } from "../../../theme/tokens";
import { trackEvent } from "../../../utils/analytics";

interface IProps {
	date: string;
}

export const StaffingActionFab = ({ date }: IProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();

	const handleToggle = () => {
		setIsOpen(!isOpen);
		trackEvent("action_fab_toggled", { is_open: !isOpen });
	};

	const handleAction = (callback: () => void, option: string) => {
		trackEvent("action_fab_option_clicked", { option });
		callback();
		setIsOpen(false);
	};

	const menuItems = [
		{
			icon: <MessageIcon />,
			text: "Send SMS Message",
			onClick: () => {
				const path = `/${chosenDepartment.department_id}/send-message`;
				navigate(path);
			},
		},
		{
			icon: <AddCircleOutlineIcon />,
			text: "Post New Shift",
			onClick: () => {
				const path = `/${chosenDepartment.department_id}/post-shift/${date}`;
				navigate(path);
			},
		},
		{
			icon: <LocalHospitalOutlinedIcon />,
			text: "Add To Shift",
			onClick: () => {
				const path = `/${chosenDepartment.department_id}/add-nurse-to-shift/${date}`;
				navigate(path);
			},
		},
		{
			icon: <PersonAdd />,
			text: "Create Staff Member",
			onClick: () => {
				const path = `/${chosenDepartment.department_id}/add-nurse/`;
				navigate(path);
			},
		},
	];

	return (
		<Box
			sx={{
				position: "fixed",
				bottom: 16,
				right: 16,
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-end",
				gap: 2,
			}}
		>
			<Box
				sx={{
					position: "absolute",
					bottom: "100%",
					right: 0,
					display: "flex",
					flexDirection: "column",
					gap: 2,
					visibility: isOpen ? "visible" : "hidden",
					opacity: isOpen ? 1 : 0,
					transition: "visibility 0s, opacity 0.2s ease-out",
					transitionDelay: isOpen ? "0s" : "0.8s",
					pointerEvents: isOpen ? "auto" : "none",
					marginBottom: 2,
				}}
			>
				{menuItems.map((item, index) => (
					<Button
						key={item.text}
						variant="contained"
						onClick={() => handleAction(item.onClick, item.text)}
						endIcon={item.icon}
						sx={{
							borderRadius: 50,
							bgcolor: "white",
							color: INK.PRIMARY,
							boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
							textTransform: "none",
							px: 3,
							py: 1.5,
							minWidth: "auto",
							width: "auto",
							alignSelf: "flex-end",
							whiteSpace: "nowrap",
							"&:hover": {
								bgcolor: NEUTRAL[100],
							},
							transition: "all 0.2s ease-out",
							opacity: isOpen ? 1 : 0,
							transform: isOpen ? "translateY(0)" : "translateY(20px)",
							transitionDelay: isOpen ? `${index * 0.1}s` : `${(menuItems.length - 1 - index) * 0.1}s`,
						}}
					>
						{item.text}
					</Button>
				))}
			</Box>
			<Fab
				color="primary"
				aria-label="add"
				onClick={handleToggle}
				sx={{
					transition: "all 0.3s ease",
					"& .MuiSvgIcon-root": {
						transition: "transform 0.3s ease",
						transform: isOpen ? "rotate(45deg)" : "rotate(180deg)",
					},
				}}
			>
				<AddIcon />
			</Fab>
		</Box>
	);
};
